import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../../hooks/useAltHotkeys';
import { ArrowTopRightOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import userSettingsService from '../../services/userSettings.service';

export interface DraggableModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | React.ReactNode;
  children: React.ReactNode;
  /** Modal width in px (default 640). When resizable, used as the initial width. */
  width?: number;
  /** Max height in px (default: viewport height - 80). Ignored when resizable=true. */
  maxHeight?: number;
  /** Initial height in px when resizable (default: viewport height - 120). */
  initialHeight?: number;
  /** Enable drag-corner resizing. When true, the panel has a fixed height and a
   *  resize grip in the bottom-right corner. */
  resizable?: boolean;
  /** Min width in px when resizable (default 320). */
  minWidth?: number;
  /** Min height in px when resizable (default 240). */
  minHeight?: number;
  /** Hide modal entirely (display: none) — used during screenshot capture */
  forceHidden?: boolean;
  /** Distance in px from the top of the viewport on first open (default 50).
   *  Horizontal placement is always centered. Clamped so the header stays
   *  on-screen. Ignored once persisted geometry (persistKey) applies. */
  topOffset?: number;
  /** Custom className for the panel */
  className?: string;
  /** Custom className for the content area */
  contentClassName?: string;
  /** Override header styling (bg, text, border). Layout classes (flex, padding,
   *  cursor-move, etc.) are always applied. */
  headerClassName?: string;
  /** Footer content */
  footer?: React.ReactNode;
  /** When provided, renders a pop-out icon in the header that triggers this callback */
  onPopOut?: () => void;
  /** Tooltip / aria-label for the pop-out button (default: "Open in new window") */
  popOutLabel?: string;
  /** When set, the modal's {x, y, width, height} get persisted to user settings
   *  under `modal_geometry_<persistKey>`. Loaded on first open, saved on close. */
  persistKey?: string;
}

interface PersistedGeometry {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

const HEADER_VISIBLE_BUFFER = 40;

export const DraggableModal: React.FC<DraggableModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = 640,
  maxHeight,
  initialHeight,
  resizable = false,
  minWidth = 320,
  minHeight = 240,
  forceHidden = false,
  topOffset = 50,
  className = '',
  contentClassName,
  headerClassName = 'border-b border-slate-600 bg-slate-700 text-white rounded-t-lg',
  footer,
  onPopOut,
  popOutLabel = 'Open in new window',
  persistKey,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const resizeStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [hasCenteredOnce, setHasCenteredOnce] = useState(false);
  const persistedRef = useRef<PersistedGeometry | null>(null);
  const persistLoadedRef = useRef(false);

  // Load persisted geometry on first open (when persistKey is set).
  useEffect(() => {
    if (!isOpen || !persistKey || persistLoadedRef.current) return;
    persistLoadedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const setting = await userSettingsService.getUserSetting(`modal_geometry_${persistKey}`);
        if (cancelled) return;
        const value = setting?.setting_value as PersistedGeometry | undefined;
        if (value) {
          persistedRef.current = value;
          if (resizable && typeof value.width === 'number' && typeof value.height === 'number') {
            setSize({
              width: Math.min(window.innerWidth, Math.max(minWidth, value.width)),
              height: Math.min(window.innerHeight, Math.max(minHeight, value.height)),
            });
          }
          if (typeof value.x === 'number' && typeof value.y === 'number') {
            setPosition({
              x: Math.min(Math.max(0, value.x), Math.max(0, window.innerWidth - HEADER_VISIBLE_BUFFER)),
              y: Math.min(Math.max(0, value.y), Math.max(0, window.innerHeight - HEADER_VISIBLE_BUFFER)),
            });
            setHasCenteredOnce(true);
          }
        }
      } catch (err) {
        // Non-fatal — fall through to default sizing/centering.
        console.warn('Failed to load modal geometry', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, persistKey, resizable, minWidth, minHeight]);

  // Initialize size when resizable opens (so width/initialHeight become real px)
  useLayoutEffect(() => {
    if (!isOpen || forceHidden || !resizable || size) return;
    const initH = initialHeight ?? Math.max(minHeight, window.innerHeight - 120);
    setSize({ width: Math.max(minWidth, width), height: initH });
  }, [isOpen, forceHidden, resizable, size, width, initialHeight, minWidth, minHeight]);

  // Place horizontally centered, `topOffset` px from the top, on first open.
  useLayoutEffect(() => {
    if (!isOpen || forceHidden || hasCenteredOnce) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const x = Math.max(0, Math.round((window.innerWidth - rect.width) / 2));
    // Clamp to the same bound dragging uses, so a large offset on a short
    // viewport can't drop the header (the only drag handle) off the bottom.
    const y = Math.max(0, Math.min(topOffset, window.innerHeight - HEADER_VISIBLE_BUFFER));
    setPosition({ x, y });
    setHasCenteredOnce(true);
  }, [isOpen, forceHidden, hasCenteredOnce, topOffset]);

  // Reset centering / size when fully closed so next open re-centers (or
  // re-loads persisted geometry).
  useEffect(() => {
    if (!isOpen) {
      setHasCenteredOnce(false);
      setPosition(null);
      setSize(null);
      persistLoadedRef.current = false;
      persistedRef.current = null;
    }
  }, [isOpen]);

  // Track latest geometry in refs so we can save on close without re-firing the
  // save effect on every drag/resize tick.
  const latestPosRef = useRef<{ x: number; y: number } | null>(null);
  const latestSizeRef = useRef<{ width: number; height: number } | null>(null);
  useEffect(() => { latestPosRef.current = position; }, [position]);
  useEffect(() => { latestSizeRef.current = size; }, [size]);

  // Persist geometry when the modal closes (cleanup runs once: when isOpen
  // flips to false, or on unmount while still open).
  useEffect(() => {
    if (!isOpen || !persistKey) return;
    return () => {
      const pos = latestPosRef.current;
      const sz = latestSizeRef.current;
      if (!pos && !sz) return;
      const value: PersistedGeometry = {
        ...(pos ? { x: pos.x, y: pos.y } : {}),
        ...(sz ? { width: sz.width, height: sz.height } : {}),
      };
      userSettingsService
        .saveUserSetting(`modal_geometry_${persistKey}`, value)
        .catch((err) => console.warn('Failed to save modal geometry', err));
    };
  }, [isOpen, persistKey]);

  // Escape closes
  useEffect(() => {
    if (!isOpen || forceHidden) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, forceHidden, onClose]);

  // Keep within viewport on resize
  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      setSize((prev) => {
        if (!prev) return prev;
        return {
          width: Math.min(prev.width, window.innerWidth),
          height: Math.min(prev.height, window.innerHeight),
        };
      });
      setPosition((prev) => {
        if (!prev) return prev;
        const panel = panelRef.current;
        if (!panel) return prev;
        const rect = panel.getBoundingClientRect();
        const maxX = Math.max(0, window.innerWidth - rect.width);
        const maxY = Math.max(0, window.innerHeight - HEADER_VISIBLE_BUFFER);
        return {
          x: Math.min(prev.x, maxX),
          y: Math.min(prev.y, maxY),
        };
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [isOpen]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only left mouse button / touch / pen
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    // Don't start drag from interactive header elements (close button)
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;

    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: e.pointerId,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();

    const rawX = e.clientX - drag.offsetX;
    const rawY = e.clientY - drag.offsetY;

    // Constrain so the modal can't be dragged fully off-screen
    const minX = -(rect.width - HEADER_VISIBLE_BUFFER);
    const maxX = window.innerWidth - HEADER_VISIBLE_BUFFER;
    const minY = 0;
    const maxY = window.innerHeight - HEADER_VISIBLE_BUFFER;

    setPosition({
      x: Math.min(Math.max(rawX, minX), maxX),
      y: Math.min(Math.max(rawY, minY), maxY),
    });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    dragStateRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (!size) return;
    e.stopPropagation();
    resizeStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: size.width,
      startHeight: size.height,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = resizeStateRef.current;
    if (!r || r.pointerId !== e.pointerId) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const maxW = Math.max(minWidth, window.innerWidth - rect.left - 8);
    const maxH = Math.max(minHeight, window.innerHeight - rect.top - 8);
    const nextWidth = Math.min(maxW, Math.max(minWidth, r.startWidth + (e.clientX - r.startX)));
    const nextHeight = Math.min(maxH, Math.max(minHeight, r.startHeight + (e.clientY - r.startY)));
    setSize({ width: nextWidth, height: nextHeight });
  };

  const handleResizePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = resizeStateRef.current;
    if (!r || r.pointerId !== e.pointerId) return;
    resizeStateRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  // Shell: Alt+C closes. Content is caller-supplied, so there's no save to bind;
  // a consumer that adds one claims the layer after us and wins.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  if (!isOpen) return null;

  const computedMaxHeight = maxHeight ?? window.innerHeight - 80;

  const style: React.CSSProperties = resizable
    ? {
        width: size?.width ?? width,
        height: size?.height ?? (initialHeight ?? window.innerHeight - 120),
        visibility: position && size ? 'visible' : 'hidden',
        transform: position ? `translate(${position.x}px, ${position.y}px)` : 'translate(0, 0)',
        display: forceHidden ? 'none' : undefined,
      }
    : {
        width,
        maxHeight: computedMaxHeight,
        // Hide initially until centered to avoid a flash in the corner
        visibility: position ? 'visible' : 'hidden',
        transform: position ? `translate(${position.x}px, ${position.y}px)` : 'translate(0, 0)',
        display: forceHidden ? 'none' : undefined,
      };

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-labelledby="draggable-modal-title"
      className={`fixed top-0 left-0 z-[100] flex flex-col rounded-lg border border-theme-border-primary bg-theme-bg-secondary shadow-2xl ${className}`}
      style={style}
    >
      {/* Header (drag handle) */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        className={`flex items-start justify-between px-4 py-3 sm:px-6 cursor-move select-none touch-none ${headerClassName}`}
      >
        <h2
          id="draggable-modal-title"
          className="cursor-text select-text text-base font-semibold text-inherit"
          style={{ paddingTop: '2px' }}
          // Title text is copyable; the surrounding header remains the drag handle.
          onPointerDown={(event) => event.stopPropagation()}
        >
          {title}
        </h2>
        <div className="ml-3 flex h-7 items-center gap-2">
          {onPopOut && (
            <button
              type="button"
              onClick={onPopOut}
              title={popOutLabel}
              aria-label={popOutLabel}
              className="relative rounded-md text-slate-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
            >
              <span className="absolute -inset-2.5"></span>
              <ArrowTopRightOnSquareIcon className="size-5" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="relative rounded-md text-slate-300 hover:text-white focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-colors"
          >
            <span className="absolute -inset-2.5"></span>
            <span className="sr-only">Close</span>
            <XMarkIcon className="size-6" aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={contentClassName || 'flex-1 overflow-y-auto px-4 py-6 sm:px-6'}>
        {children}
      </div>

      {/* Footer */}
      {footer && (
        <div className="px-4 py-4 sm:px-6 border-t border-theme-border-primary bg-theme-bg-secondary rounded-b-lg">
          {footer}
        </div>
      )}

      {/* Resize grip (bottom-right) */}
      {resizable && (
        <div
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize select-none touch-none"
          style={{
            background:
              'linear-gradient(135deg, transparent 0%, transparent 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 60%, transparent 60%, transparent 70%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0.25) 80%, transparent 80%)',
          }}
          aria-label="Resize"
        />
      )}
    </div>,
    document.body,
  );
};

export default DraggableModal;
